import { useState } from 'react';
import { useCallback } from 'react';
import { useEffect } from 'react';

import { useActivityTimeout } from '@crossnokaye/ux-common';

import { AUTH_SCOPE_READ_ONLY } from '@/constants';
import { tokenIsViewOnly } from '@/providers/auth/helper';

/**
 * Custom hook to manage view-only state
 *
 * @param isAuthenticated - Whether the user is authenticated
 * @param getAccessTokenSilently - Function to get the access token
 * @returns {isViewOnly: boolean, requestViewOnlyToken: () => Promise<void>}
 *
 */
export const useViewOnlyState = (
  isAuthenticated: boolean,
  getAccessTokenSilently: (options?: any) => Promise<string>,
  loginWithRedirect?: () => Promise<void>
) => {
  const [isViewOnly, setIsViewOnly] = useState(false);

  const requestViewOnlyToken = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently({
        cacheMode: 'off',
        authorizationParams: {
          // this is a bug in auth0, it should be scope, not scopes
          // but scope is not available in the auth0 event, but a custom property is
          scopes: AUTH_SCOPE_READ_ONLY,
        },
      });
      setIsViewOnly(tokenIsViewOnly(token));
    } catch (error: any) {
      console.error('Failed to request view only token', error);
      // assume the user is view only if we fail to fetch the token
      setIsViewOnly(true);
    }
  }, [getAccessTokenSilently, loginWithRedirect]);

  // Check view-only status when auth state changes
  useEffect(() => {
    if (!isAuthenticated) {
      setIsViewOnly(false);
      return;
    }

    getAccessTokenSilently()
      .then((token) => setIsViewOnly(tokenIsViewOnly(token)))
      .catch((error) => {
        console.error('Failed to fetch token for view only check', error);
        // assume the user is view only if we fail to fetch the token
        setIsViewOnly(true);
      });
  }, [isAuthenticated, getAccessTokenSilently, loginWithRedirect]);

  useActivityTimeout({
    onTimeout: requestViewOnlyToken,
    oneShot: true,
  });

  return { isViewOnly, requestViewOnlyToken };
};
